import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { filter, map, Observable } from "rxjs";

import {
    VtoProduct,
    VtoType,
    YmkSdkVtoConfiguration,
} from "@hermes/api-model-virtual-try-on";

import * as vtoActions from "../actions/vto.actions";

import {
    VTO_CONFIGURATION_UNDEFINED,
    VtoPageType,
} from "../constants/vto.constant";
import {
    LocalizedVtoCategory,
    LocalizedVtoSubcategory,
    VtoCategoriesConfiguration,
    VtoModalConfiguration,
} from "../model/localized-vto-category.model";
import { VtoState } from "../reducers/vto-state";
import {
    selectActiveCategory,
    selectActiveSubcategory,
    selectCameraDimensionState,
    selectDisplayLearnMoreLink,
    selectIsActiveSubcategoryProductSelected,
    selectIsApprovalState,
    selectLocalizedVtoCategories,
    selectModalStatusState,
    selectPageType,
    selectSdkErrorState,
    selectAppliedConfiguration,
    selectVtoConfiguration,
    selectVtoNoShadeOrNoSelect,
    selectVtoProductSelected,
    vtoModalConfiguration,
    selectProductVtoType,
} from "../selectors/vto.selectors";
import {
    VtoCameraDimension,
    VtoModalStatus,
    VtoSdkError,
} from "../types/vto.types";

@Injectable()
export class VtoFacade {
    // Selectors
    public vtoConfiguration$: Observable<YmkSdkVtoConfiguration> = this.store
        .select(selectVtoConfiguration)
        .pipe(
            filter(
                (vtoConfiguration) =>
                    vtoConfiguration !== VTO_CONFIGURATION_UNDEFINED,
            ),
            map(
                (vtoConfiguration) =>
                    vtoConfiguration as YmkSdkVtoConfiguration,
            ),
        );

    public vtoNoShadeOrNoSelectSelected$: Observable<boolean> =
        this.store.select(selectVtoNoShadeOrNoSelect);

    public modalStatus$: Observable<VtoModalStatus> = this.store.select(
        selectModalStatusState,
    );

    public cameraDimension$: Observable<VtoCameraDimension> = this.store.select(
        selectCameraDimensionState,
    );

    public isApproval$: Observable<boolean> = this.store.select(
        selectIsApprovalState,
    );

    public sdkError$: Observable<VtoSdkError> =
        this.store.select(selectSdkErrorState);

    public productVtoType$: Observable<VtoType> =
        this.store.select(selectProductVtoType);

    public localizedVtoCategories$: Observable<LocalizedVtoCategory[]> =
        this.store.select(selectLocalizedVtoCategories);

    public activeCategory$: Observable<LocalizedVtoCategory> =
        this.store.select(selectActiveCategory);

    public activeSubcategory$: Observable<LocalizedVtoSubcategory> =
        this.store.select(selectActiveSubcategory);

    public displayLearnMoreLink$: Observable<boolean> = this.store.select(
        selectDisplayLearnMoreLink,
    );
    public isActiveSubcategoryProductSelected$: Observable<boolean> =
        this.store.select(selectIsActiveSubcategoryProductSelected);

    public vtoProductSelected$: Observable<VtoProduct | undefined> =
        this.store.select(selectVtoProductSelected);

    public selectAppliedConfiguration$: Observable<VtoCategoriesConfiguration> =
        this.store.select(selectAppliedConfiguration);

    public vtoModalConfiguration$: Observable<VtoModalConfiguration> =
        this.store.select(vtoModalConfiguration);

    public pageType$: Observable<string> = this.store.select(selectPageType);

    constructor(private store: Store<VtoState>) {}

    // Actions
    public fetchVtoConfiguration(): void {
        this.store.dispatch(vtoActions.fetchVtoConfiguration());
    }

    public changeScreenOrientation(): void {
        this.store.dispatch(vtoActions.changeScreenOrientation());
    }

    public userApprove(): void {
        this.store.dispatch(vtoActions.userApprove());
    }

    public userDisapprove(): void {
        this.store.dispatch(vtoActions.userDisapprove());
    }

    public checkTimeOut(): void {
        this.store.dispatch(vtoActions.checkTimeOut());
    }

    public launchCamera(): void {
        this.store.dispatch(vtoActions.launchCamera());
    }

    public closeModal(): void {
        this.store.dispatch(vtoActions.closeModal());
    }

    public openModal(pageType: string, eventLabel?: string): void {
        this.store.dispatch(vtoActions.openModal({ pageType, eventLabel }));
    }

    public destroyModal(): void {
        this.store.dispatch(vtoActions.destroyModal());
    }

    public onAir(): void {
        this.store.dispatch(vtoActions.onAir());
    }

    public catchSdkError(sdkError: VtoSdkError): void {
        this.store.dispatch(vtoActions.catchSdkError({ sdkError }));
    }

    public updateCameraDimension(cameraDimension: VtoCameraDimension): void {
        this.store.dispatch(
            vtoActions.updateCameraDimension({ cameraDimension }),
        );
    }

    public fetchLocalizedVtoCategories(): void {
        this.store.dispatch(vtoActions.fetchLocalizedVtoCategories());
    }

    public updateSelectedSkuVariantOnDisplay(currentVtoSku: string): void {
        this.store.dispatch(
            vtoActions.updateSelectedSkuVariantOnDisplay({
                currentVtoSku,
            }),
        );
    }

    public updateActiveSubcategory(
        vtoSubcategory: LocalizedVtoSubcategory,
    ): void {
        this.store.dispatch(
            vtoActions.updateActiveSubcategory({
                vtoSubcategory,
            }),
        );
    }

    public updateActiveCategory(vtoCategory: LocalizedVtoCategory): void {
        this.store.dispatch(
            vtoActions.updateActiveCategory({
                vtoCategory,
            }),
        );
    }

    public learnMoreRedirect(
        vtoCategory: LocalizedVtoCategory,
        vtoSubcategory: LocalizedVtoSubcategory,
    ) {
        this.store.dispatch(
            vtoActions.learnMoreRedirect({
                vtoCategory,
                vtoSubcategory,
            }),
        );
    }

    public updatePageType(pageType: VtoPageType): void {
        this.store.dispatch(vtoActions.updatePageType({ pageType }));
    }

    public updateCurrentVtoType(vtoType: VtoType): void {
        this.store.dispatch(
            vtoActions.updateCurrentVtoType({ currentVtoType: vtoType }),
        );
    }
}
